import type { Viewer } from '../../viewer';
import { FeatureCommandDependencyIndex } from '../command/FeatureCommandDependencyIndex';
import { featureCommandAdapterRegistry } from '../command/FeatureCommandAdapterRegistry';
import { FeatureEditSession } from './FeatureEditSession';
import { FeatureEditSessionManager } from './FeatureEditSessionManager';
import type { FeatureExecutionRuntime } from '../execution/FeatureExecutionRuntime';
import type { FeaturePrototype } from '../prototype/FeaturePrototype';

/**
 * 特征编辑编排器。
 */
export class FeatureEditOrchestrator {
  /** Viewer 实例。 */
  private readonly viewer: Viewer;
  /** 特征执行运行时。 */
  private readonly runtime: FeatureExecutionRuntime;
  /** 特征编辑会话管理器。 */
  private readonly featureEditSessionManager: FeatureEditSessionManager;

  /**
   * @param viewer Viewer 实例。
   * @param runtime 特征执行运行时。
   */
  public constructor(viewer: Viewer, runtime: FeatureExecutionRuntime) {
    this.viewer = viewer;
    this.runtime = runtime;
    this.featureEditSessionManager = new FeatureEditSessionManager();
  }

  /**
   * 读取指定特征命令。
   *
   * @param featureId 特征命令 ID。
   * @returns 命令快照；未找到时返回 null。
   */
  public async getFeatureCommandById(featureId: string): Promise<CadCommand | null> {
    const stateSummary = await this.runtime.getGeometryStateSummary();
    const command = stateSummary.history.find((item) => item.id === featureId) ?? null;
    return command ? structuredClone(command) : null;
  }

  /**
   * 从命令历史恢复指定特征的原型对象。
   *
   * 设计说明：
   * 1. 原型恢复统一收口在 cad-core，避免 web-client 再直接解析 `CadCommand.params`；
   * 2. 特征种类分派统一通过 command adapter registry 完成；
   * 3. 后续新增其它特征时，只需扩展适配器与 prototype，不再复制编辑恢复流程。
   *
   * @param featureId 特征 ID。
   * @returns 特征原型；不支持或未找到时返回 null。
   */
  public async getFeaturePrototypeById(featureId: string): Promise<FeaturePrototype | null> {
    const command = await this.getFeatureCommandById(featureId);
    if (!command) {
      return null;
    }
    return featureCommandAdapterRegistry.createPrototypeFromCommand(command);
  }

  /**
   * 启动指定特征的编辑会话。
   *
   * 设计说明：
   * 1. 会话是依赖隔离预览沙箱的统一入口；
   * 2. 预览命令只移除目标特征及其真实下游，历史位置靠后但无依赖的其它实体继续保留；
   * 3. 原始完整命令与影响闭包同时保存在会话中，提交和取消都能从同一权威快照恢复；
   * 4. 同一特征重复进入编辑时，会覆盖旧会话，保证活动会话唯一。
   *
   * @param featureId 特征 ID。
   * @returns 编辑会话；当前特征暂不支持编辑时返回 null。
   */
  public async startFeatureEditSession(featureId: string): Promise<FeatureEditSession | null> {
    const stateSummary = await this.runtime.getGeometryStateSummary();
    const commandIndex = stateSummary.history.findIndex((item) => item.id === featureId);
    if (commandIndex < 0) {
      return null;
    }

    const targetCommand = stateSummary.history[commandIndex];
    if (!targetCommand) {
      return null;
    }
    const prototype = featureCommandAdapterRegistry.createPrototypeFromCommand(targetCommand);
    if (!prototype) {
      return null;
    }

    const dependencyIndex = new FeatureCommandDependencyIndex({
      commands: stateSummary.history,
      objects: stateSummary.objects,
    });
    const affectedFeatureIds = dependencyIndex.resolveDependentFeatureIds(featureId, true);
    const affectedFeatureIdSet = new Set(affectedFeatureIds);
    const originalCommands = stateSummary.history.map((command) => structuredClone(command));
    const previewCommands = stateSummary.history
      .filter((command) => !affectedFeatureIdSet.has(command.id))
      .map((command) => structuredClone(command));

    return this.featureEditSessionManager.createSession(
      prototype,
      prototype.getDefinition(),
      {
        targetCommand,
        originalCommands,
        previewCommands,
        affectedFeatureIds,
      },
    );
  }

  /**
   * 读取指定特征的活动编辑会话。
   *
   * @param featureId 特征 ID。
   * @returns 编辑会话；不存在时返回 null。
   */
  public getFeatureEditSession(featureId: string): FeatureEditSession | null {
    return this.featureEditSessionManager.getSession(featureId);
  }

  /**
   * 判断当前是否存在活动特征编辑会话。
   *
   * @returns 存在任一活动会话时返回 true。
   */
  public hasActiveFeatureEditSession(): boolean {
    return this.featureEditSessionManager.hasActiveSession();
  }

  /**
   * 关闭指定特征的编辑会话。
   *
   * @param featureId 特征 ID。
   */
  public closeFeatureEditSession(featureId: string): void {
    this.featureEditSessionManager.closeSession(featureId);
  }

  /**
   * 用新的命令定义替换指定特征，并重放整条命令历史。
   *
   * 设计说明：
   * 1. 编辑既有特征时必须保留原特征 ID，保证设计树与依赖关系稳定；
   * 2. 通过整批回放统一重建结果体，避免局部改状态导致 Worker 与 Viewer 脱节；
   * 3. 回放完成后同步更新依赖图与场景 mesh，保证后续草图驱动重建仍能正常工作。
   *
   * @param featureId 目标特征 ID。
   * @param nextCommand 替换后的命令。
   * @returns 是否替换成功。
   * @throws 当命令历史中不存在指定的待编辑特征命令时。
   */
  public async replaceFeatureCommand(featureId: string, nextCommand: CadCommand): Promise<boolean> {
    const activeSession = this.featureEditSessionManager.getSession(featureId);
    let nextCommands: CadCommand[];
    let rollbackCommands: CadCommand[];
    let affectedCommandIds: string[];
    let beforeSummary: WorkerGeometryStateSummary;

    if (activeSession) {
      beforeSummary = await this.runtime.getGeometryStateSummary();
      rollbackCommands = activeSession.getPreviewCommands();
      affectedCommandIds = activeSession.getAffectedFeatureIds();
      const replacedCommand = this.preserveFeatureCommandCreatedAt(
        nextCommand,
        activeSession.getTargetCommand(),
      );
      replacedCommand.id = featureId;
      nextCommands = activeSession.getOriginalCommands().map((command) => (
        command.id === featureId ? replacedCommand : command
      ));
    } else {
      const stateSummary = await this.runtime.getGeometryStateSummary();
      beforeSummary = stateSummary;
      const commandIndex = stateSummary.history.findIndex((item) => item.id === featureId);
      if (commandIndex < 0) {
        throw new Error(`未找到待编辑的特征命令：${featureId}`);
      }

      rollbackCommands = stateSummary.history;
      affectedCommandIds = new FeatureCommandDependencyIndex({
        commands: stateSummary.history,
        objects: stateSummary.objects,
      }).resolveDependentFeatureIds(featureId, true);
      const originalCommand = stateSummary.history[commandIndex];
      const replacedCommand = this.preserveFeatureCommandCreatedAt(nextCommand, originalCommand);
      replacedCommand.id = featureId;

      nextCommands = stateSummary.history.map((command, index) => {
        if (index !== commandIndex) {
          return structuredClone(command);
        }
        return replacedCommand;
      });
    }

    const replayResult = await this.runtime.replayFeatureCommands({
      commands: nextCommands,
      clearFirst: true,
      targetFeatureId: featureId,
      rollbackCommands,
      syncHistoryProjection: true,
    });

    await this.viewer.syncAffectedFeatureMeshesFromState({
      beforeSummary,
      afterSummary: replayResult.summary,
      affectedCommandIds,
    });
    if (activeSession) {
      this.featureEditSessionManager.closeSession(featureId);
    }
    return true;
  }

  /**
   * 保留特征命令的首次创建时间戳。
   *
   * 设计说明：
   * 1. 设计树按 `timestamp` 排序时间线，编辑特征时只能更新参数，不能刷新创建时间；
   * 2. 新命令对象通常会在适配阶段写入 `Date.now()`，因此必须在替换入口统一回写原始时间戳；
   * 3. 该逻辑只作用于编辑替换，不影响首次创建时写入的新时间戳。
   *
   * @param nextCommand 编辑后生成的新命令。
   * @param originalCommand 被替换前的原始命令。
   * @returns 已保留创建时间戳的命令副本。
   */
  private preserveFeatureCommandCreatedAt(
    nextCommand: CadCommand,
    originalCommand: CadCommand,
  ): CadCommand {
    const preservedCommand = structuredClone(nextCommand);
    preservedCommand.timestamp = originalCommand.timestamp;
    // 编辑 UI 可以重建参数对象，但不能把既有零件特征迁移到当前活动编辑零件。
    if (originalCommand.params.documentScopeNodeKey) {
      preservedCommand.params.documentScopeNodeKey = originalCommand.params.documentScopeNodeKey;
      preservedCommand.params.documentObjectIds = [...(originalCommand.params.documentObjectIds ?? [])];
    }
    return preservedCommand;
  }

  /**
   * 释放编排器资源。
   */
  public dispose(): void {
    this.featureEditSessionManager.dispose();
  }
}
